import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Project } from 'app/modules/projects/projects.types';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { BidRound } from 'app/modules/projects/details/offers/offers.types';
import { User } from 'app/core/models/user.types';
import { Document } from 'app/shared/modules/folders/folders.types';
import { Disposition } from '../../types/dispositions.types';
import { DispositionsService } from '../../services/dispositions.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { AccountService } from 'app/core';
import { CurrencyMaskOptions, getCurrencyMask } from 'app/shared/Utils/common.utils';
import { filter } from 'rxjs/operators';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission } from '@core/enums';
import { UserState } from '@state/user/state';

@Component({
  selector: 'add-dispositions',
  templateUrl: './add-dispositions.component.html',
  styleUrls: ['./add-dispositions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddDispositionsComponent implements OnInit {
  dispositionForm: UntypedFormGroup;
  users: User[];
  disposition: Disposition;
  documents: Document[];
  project: Project;
  file_resources: FileResource[];
  bidRounds: BidRound[];
  public isLoading: boolean;

  public uploadedFile: any;
  public states: string[] = ['draft', 'submitted', 'accepted', 'rejected'];
  public documentsToUpload: any[] = [];

  private get accountCurrency(): string {
    return this._accountService.getCurrentAccount()?.display_currency;
  }

  get currencyMaskOptions(): CurrencyMaskOptions {
    return getCurrencyMask(this.accountCurrency, 0);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<AddDispositionsComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _dispositionService: DispositionsService,
    private _projectsService: ProjectsService,
    private _accountService: AccountService,
    private confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    this._accountService.currentAccount.subscribe((accountData) => {
      this._dispositionService.accountId = accountData.id;
      this._projectsService.project$.subscribe((project) => {
        this._dispositionService.projectId = project.id || data.project?.id;
      });
    });
  }

  ngOnInit(): void {
    this.dispositionForm = this._formBuilder.group({
      workflow_state: [''],
      bid_round: [''],
      amount: [''],
      user_id: [''],
      closing_period: [''],
      due_diligence_period: [''],
      earnest_money: [''],
      description: [''],
      transaction_type: [''],
      sent_at: [''],
      name: [''],
    });

    this.users = this.data.options.users;

    if (this.data.disposition) {
      this.disposition = this.data.disposition;
      this.dispositionForm.patchValue(this.disposition);
    }

    this.project = this.data.project;
    this._dispositionService.projectId = this.project.id;

    this._dispositionService.getBidRounds().subscribe((bidRounds) => {
      this.bidRounds = bidRounds;
    });
  }

  public get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.dispositionForm.pristine || this.dispositionForm.invalid;
  }

  discard(): void {
    this.matDialogRef.close();
  }

  deleteDisposition(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this._dispositionService.deleteDisposition(this.disposition.id, this.data.group).subscribe((disposition) => {
          this.matDialogRef.close({ disposition, deleted: true });
        });
      });
  }

  save(): void {
    this.dispositionForm.disable();

    let disposition = this.dispositionForm.value;
    if (this.disposition?.id) {
      disposition.id = this.disposition.id;
      this._dispositionService.updateDisposition(disposition, this.data.group).subscribe((disposition) => {
        this.matDialogRef.close({ disposition: disposition });
      });
    } else {
      this._dispositionService.createDisposition(disposition, this.data.group).subscribe((disposition) => {
        this.matDialogRef.close({ disposition: disposition });
      });
    }
  }

  // Function attached to debounce-click directive to flag the button's disable state.
  public isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  canDeleteDisposition(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DISPOSITIONS_DELETE));
    }

    return true;
  }
}
